import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';
import type { CSSObject } from 'styled-components';

import {
  matchesTokenCompoundVariant,
  mergeTokenStyles,
  useTheme,
} from '@adsk/alloy-react-styles';
import { stylePropType, StyleProp } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { DATE_PICKER_CELL_VARIANTS } from '../consts';
import { CellVariant } from '../types';

type CellProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'style'
> & {
  style?: StyleProp;
  variant?: CellVariant;
  selected?: boolean;
  today?: boolean;
  hasEvent?: boolean;
  tooltipContent?: ReactNode;
  [dataAttr: `data-${string}`]: string | undefined;
};

const VARIANT_KEY_MAP: Record<string, string> = {
  [DATE_PICKER_CELL_VARIANTS.DEFAULT]: 'default',
  [DATE_PICKER_CELL_VARIANTS.RANGE_MIDDLE]: 'rangeMiddle',
  [DATE_PICKER_CELL_VARIANTS.RANGE_START]: 'rangeStart',
  [DATE_PICKER_CELL_VARIANTS.RANGE_END]: 'rangeEnd',
  [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_MIDDLE]: 'outsideRangeMiddle',
  [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_START]: 'outsideRangeStart',
  [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_END]: 'outsideRangeEnd',
  [DATE_PICKER_CELL_VARIANTS.OUTSIDE]: 'outside',
  [DATE_PICKER_CELL_VARIANTS.SPACER]: 'spacer',
};

const CELL_RESET = {
  appearance: 'none',
  border: 'none',
  backgroundColor: 'transparent',
  padding: 0,
  margin: 0,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
} as const;

const Cell = React.forwardRef<HTMLButtonElement, CellProps>(
  (
    {
      className,
      style,
      variant = DATE_PICKER_CELL_VARIANTS.DEFAULT,
      selected,
      today,
      disabled,
      hasEvent,
      tooltipContent,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;

    const variantKey = VARIANT_KEY_MAP[variant] ?? 'default';
    const isDefaultVariant = variant === DATE_PICKER_CELL_VARIANTS.DEFAULT;

    const cellStyle = useMemo(() => {
      if (!t?.cell) {
        return undefined;
      }

      const parts: CSSObject[] = [
        t.cell._ as CSSObject,
        t.cell.variant?.[
          variantKey as keyof typeof t.cell.variant
        ] as CSSObject,
      ];

      if (isDefaultVariant && today && t.cell.today?.true) {
        parts.push(t.cell.today.true as CSSObject);
      }

      if (disabled && t.cell.disabled?.true) {
        parts.push(t.cell.disabled.true as CSSObject);
      }

      if (isDefaultVariant && selected && t.cell.selected?.true) {
        parts.push(t.cell.selected.true as CSSObject);
      }

      const resolvedVariantState = {
        variant: variantKey,
        disabled: !!disabled,
        selected: isDefaultVariant && !!selected,
        today: isDefaultVariant && !!today,
      };

      if (isDefaultVariant) {
        t.cell.compoundVariants?.forEach((compound) => {
          if (matchesTokenCompoundVariant(compound, resolvedVariantState)) {
            parts.push(compound.styles as CSSObject);
          }
        });
      }

      return mergeTokenStyles(...parts);
    }, [t, variantKey, disabled, selected, today, isDefaultVariant]);

    const eventDotStyle = useMemo(
      () =>
        mergeTokenStyles(
          t?.eventDot?._ as CSSObject,
          (selected
            ? t?.eventDot?.selected?.true
            : t?.eventDot?.selected?.false) as CSSObject,
        ),
      [t, selected],
    );

    const cellWidth = t?.cellWidth ?? t?.cellHeight;
    const cellHeight = t?.cellHeight;

    const children = (
      <button
        type="button"
        {...props}
        ref={ref}
        className={className}
        disabled={disabled}
        tabIndex={variant === DATE_PICKER_CELL_VARIANTS.SPACER ? -1 : undefined}
        css={[
          CELL_RESET,
          {
            width: cellWidth,
            height: cellHeight,
            cursor: disabled ? 'default' : 'pointer',
          },
          cellStyle,
          style ?? undefined,
        ]}
      >
        {props.children}
        {hasEvent && <div css={eventDotStyle} />}
      </button>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          variant={Tooltip.VARIANTS.DARK}
          placement={Tooltip.PLACEMENTS.TOP}
          content={tooltipContent}
          delayShow={0}
          delayHide={0}
          getTargetProps={() => ({
            style: {
              width: cellWidth,
              height: cellHeight,
            },
          })}
        >
          {children}
        </Tooltip>
      );
    }

    return children;
  },
);

Cell.displayName = 'Cell';

Cell.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Day value */
  children: PropTypes.any,
  /**  The style variant */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_CELL_VARIANTS)),
  /** Whether this day is selected, does not apply when variant is a range variant */
  selected: PropTypes.bool,
  /** Whether this day is the today day, does not apply when variant is a range variant */
  today: PropTypes.bool,
  /** Whether this day is the restricted */
  disabled: PropTypes.bool,
  /** Tooltip content */
  tooltipContent: PropTypes.any,
};

export default Object.assign(Cell, { VARIANTS: DATE_PICKER_CELL_VARIANTS });
